import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "./colors";
import { PageContainer } from "./PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const SECTION_SX = {
  position: "relative",
  paddingTop: "6rem",
  paddingBottom: "6rem",
  px: { xs: 2, md: 4 },
  backgroundColor: HERO_BG,
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const LABEL_SX = {
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
  mb: 1.5,
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: colors.primary.main,
  "&::before": {
    content: '""',
    display: "block",
    width: 24,
    height: "1px",
    background: colors.primary.main,
  },
};

const TITLE_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 0,
  "& span": {
    background: `
      linear-gradient(
        180deg,
        #f7cb79 0%,
        #f0b458 40%,
        #de8f2c 100%
      )
    `,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
};

const DESCRIPTION_SX = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
}

const STEPS_SX = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: 0,
  maxWidth: "45rem",
  mx: "auto",
  mt: { xs: "3rem", md: "5rem" },
};

const STEP_SX = {
  display: "flex",
  gap: { xs: "1.5rem", md: "3rem" },
  py: { xs: "2rem", md: "2.5rem" },
  pl: { xs: "2rem", md: "3rem" },
  "&:not(:last-child)": {
    marginBottom: "2rem",
  },
  background: `
    linear-gradient(
      180deg,
      rgba(255,255,255,0.02),
      transparent
    )
  `,
  border: "1px solid rgba(255,255,255,0.04)",
  borderRadius: "18px",
  padding: {
    xs: "1.8rem",
    md: "2.25rem",
  },
};

const STEP_NUMBER_BLOCK_SX = {
  minWidth: "52px",
  display: "flex",
  justifyContent: "flex-start",
  borderTop: "1px solid rgba(232,163,61,0.25)",
  paddingTop: "10px",
};

const STEP_NUM_SX = {
  fontFamily: "'Inter', sans-serif",
  fontSize: "0.78rem",
  fontWeight: 700,
  letterSpacing: "0.18em",
  color: "rgba(255,255,255,0.25)",
  lineHeight: 1,
};

const STEP_TAG_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "0.58rem",
  fontWeight: 600,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: colors.primary.main,
  "&::before": {
    content: '""',
    width: 12,
    height: "1px",
    background: colors.primary.main,
  }
};

const STEP_TITLE_SX = {
  mb: 0.5,
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "1.35rem", md: "1.8rem" },
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: colors.text.primary,
};

const STEP_TEXT_SX = {
  fontSize: "0.88rem",
  fontWeight: 400,
  lineHeight: 1.7,
  color: colors.text.secondary,
};

const STEPS = [
  {
    key: "steam",
    num: "01",
    tag: "Authentication",
    title: "Steam Sign-In",
    text: "Connect your Steam account. We read your play history and CS2 hours to begin calibrating your profile baseline.",
  },
  {
    key: "identity",
    num: "02",
    tag: "Optional",
    title: "Optional ID Check",
    text: "A privacy-first ID check through didit.me adds to your reputation and makes multi-accounting structurally harder. It is optional — a Steam account is all you need to join.",
  },
  {
    key: "trust",
    num: "03",
    tag: "Adaptive",
    title: "Trust Calibration",
    text: "Five placement matches build your Trust Score across impact, behavior, reliability, and communication. Not just aim.",
  },
  {
    key: "queue",
    num: "04",
    tag: "Structured",
    title: "Queue Windows",
    text: "Matchmaking opens during focused peak windows — typically 19:00–23:00 local time — ensuring consistent lobby quality instead of thin queues at 3am.",
  },
  {
    key: "live",
    num: "05",
    tag: "Live",
    title: "Competitive Matchmaking",
    text: "Your ELO, Trust Score, and Impact Rating combine to place you in a lobby where every player belongs. Every game counts.",
  },
];

export default function HowOuroWorks() {
  return (
    <Box component="section" id="how" sx={SECTION_SX}>
      <PageContainer sx={{ maxWidth: "75rem" }}>
        <MotionBox
          {...REVEAL}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <Typography component="div" sx={LABEL_SX}>
            Process
          </Typography>
          <Typography component="h2" sx={TITLE_SX}>
            How Ouro
            <br />
            <span>Works</span>
          </Typography>
          <Typography sx={DESCRIPTION_SX}>
            Every player passes through the same qualification pipeline before entering the ecosystem.
          </Typography>
        </MotionBox>

        <Box sx={STEPS_SX}>
          {STEPS.map((step, index) => (
            <MotionBox
              key={step.key}
              {...REVEAL}
              transition={{
                duration: 0.9,
                delay: index * 0.1,
                ease: [0.22, 1, 0.36, 1],
                opacity: 0.65 + index * 0.08,
              }}
              sx={STEP_SX}
            >
              <Box sx={STEP_NUMBER_BLOCK_SX}>
                <Typography sx={STEP_NUM_SX}>
                  {step.num}
                </Typography>
              </Box>
              <Box>
                <Typography component="span" sx={STEP_TAG_SX}>
                  {step.tag}
                </Typography>
                <Typography component="h3" sx={STEP_TITLE_SX}>
                  {step.title}
                </Typography>
                <Typography component="p" sx={STEP_TEXT_SX}>
                  {step.text}
                </Typography>
              </Box>
            </MotionBox>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 5, md: 6 } }}>
          <Box component={Link} to="/how-it-works" sx={btnGhostSx}>
            Learn more →
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
